import React from 'react';
import {withRouter} from 'react-router';
import {Link} from 'react-router-dom';

import BaseView from '../base-view/BaseView';
import SubmissionDetails from '../../components/submission-details/SubmissionDetails';
import CommonConstants from '../../constants/CommonConstants';
import SearchSidebar from '../../components/search-sidebar/SearchSidebar';
import Grid from '../../components/grid/Grid';
import Loading from '../../components/loading/Loading';
import SubmissionDownloadButton from '../../components/submission-download-button/SubmissionDownloadButton';
import Thumbnail from '../../components/thumbnail/Thumbnail';
import NuxeoClient from '../../clients/NuxeoClient';
import ObjectUtils from '../../utils/ObjectUtils';
import EventUtils from '../../utils/EventUtils';
import SearchResults from '../../models/SearchResults';

import './SubmissionsView.css';

const SUBMISSION_DOWNLOAD_FORMATTER = (cell) => <SubmissionDownloadButton submissionId={cell}></SubmissionDownloadButton>;

const SUBMISSION_THUMBNAIL_FORMATTER = (cell) => <Thumbnail image={cell}/>;

const SUBMISSION_DETAIL_LINK_FORMATTER = (cell, entry) => <div className="submission-detail-link">
    <Link to={SubmissionsView.getPath({id: entry.uid})}>{cell}</Link>
</div>;

const GRID_COLUMNS = [
    {
        propertyName: 'uid',
        formatter: SUBMISSION_DOWNLOAD_FORMATTER
    }, {
        displayName: 'Submission ID',
        propertyName: 'properties.archive_submission:jobId'
    }, {
        propertyName: 'thumbnail',
        formatter: SUBMISSION_THUMBNAIL_FORMATTER
    }, {
        displayName: 'Submission Name',
        propertyName: 'title',
        formatter: SUBMISSION_DETAIL_LINK_FORMATTER
    }, {
        displayName: 'Property Name',
        propertyName: 'properties.archive_submission:propertyName'
    }, {
        displayName: 'Licensee Name',
        propertyName: 'properties.archive_submission:licenseeName'
    }, {
        displayName: 'Product Category Name',
        propertyName: 'properties.archive_submission:productCategoryName'
    }, {
        displayName: 'Product Detail Name',
        propertyName: 'properties.archive_submission:productDetailName'
    }
];

const SUBMISSION_THUMBNAIL_MAPPER = (entry) => {
    return {
        href: SubmissionsView.getPath({id: entry.uid}),
        thumbnail: entry.thumbnail,
        text: entry.title
    }
};

class SubmissionsView extends BaseView {
    static VIEW_PATH = '/submission';

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            page: CommonConstants.FILTER_FIRST_PAGE,
            searchTerm: null,
            searchResults: new SearchResults(),
            selectedSubmission: null
        }

        this._loadMoreSearchResults = this
            ._loadMoreSearchResults
            .bind(this);
        this._loadSelectedSubmission = this
            ._loadSelectedSubmission
            .bind(this);
        this._handleAddToCart = this
            ._handleAddToCart
            .bind(this);
    }

    get loading() {
        return this.state.loading;
    }

    set loading(loading) {
        this.setState({loading: loading});
    }

    get page() {
        return this.state.page;
    }

    set page(page) {
        this.setState({page: page});
    }

    get searchResults() {
        return this.state.searchResults;
    }

    set searchResults(searchResults) {
        this.setState({searchResults: searchResults});
    }

    get searchTerm() {
        return this.state.searchTerm;
    }

    set searchTerm(searchTerm) {
        this.setState({
            searchTerm: searchTerm,
            page: CommonConstants.FILTER_FIRST_PAGE,
            searchResults: new SearchResults(),
            selectedSubmission: null
        }, () => this._loadMoreSearchResults());
    }

    get selectedSubmission() {
        return this.state.selectedSubmission;
    }

    set selectedSubmission(selectedSubmission) {
        this.setState({selectedSubmission: selectedSubmission});
    }

    componentDidMount() {
        this._loadMoreSearchResults();
        this._loadSelectedSubmission(this.props.location);

        this._handleClearToCart();  //Clear Cart item when page change
    }

    componentWillReceiveProps(props) {
        if (this.props.location.search !== props.location.search) {
            this._loadSelectedSubmission(props.location);
        }
    }

    _handleClearToCart(){
        // Clear the cart item when change page
        EventUtils.raiseEvent('clear-to-cart', { items: [] }, this.props);
    }

    _loadMoreSearchResults() {
        const {searchTerm, page, searchResults} = this.state;
        this.loading = true;

        if (ObjectUtils.includesNotBlankProperty(searchTerm)) {
            NuxeoClient
                .instance()
                .getArchivedSubmissions(page, null, null, null, searchTerm)
                .then((response) => {
                    this.setState(
                        {
                            loading: false,
                            page: page + 1,
                            searchResults: new SearchResults(response, searchResults.entries)
                        },
                        () => this._searchSidebar.aggregations = this.state.searchResults.aggregations
                    );
                })
                .catch(() => {
                    window
                        .Materialize
                        .toast('Failed to retrive results.', 5000);
                    this.loading = false;
                });
        } else {
            // A call just to get the initial aggregations
            NuxeoClient
                .instance()
                .getArchivedSubmissions(page, CommonConstants.FILTER_MIN_PAGE_SIZE, null, null, searchTerm)
                .then((response) => {
                    response.entries = [];

                    this.setState(
                        {
                            loading: false,
                            searchResults: new SearchResults(response)
                        },
                        () => this._searchSidebar.aggregations = this.state.searchResults.aggregations
                    );
                })
                .catch(() => {
                    window
                        .Materialize
                        .toast('Failed to retrive filters.', 5000);
                    this.loading = false;
                });
        }
    }

    _loadSelectedSubmission(location) {
        const {id} = this.getSearch(location);
        if (id) {
            this.loading = true;

            return NuxeoClient
                .instance()
                .getArchivedSubmission(
                    id,
                    'archive_submission, dublincore, archive_file, file, picture'
                )
                .then((submission) => {
                    this.selectedSubmission = submission;
                    this.loading = false;
                })
                .catch(() => {
                    window
                        .Materialize
                        .toast('Failed to retrive submission.', 5000);
                    this.loading = false;
                });
        } else {
            this.selectedSubmission = null;
        }
    }

    _handleAddToCart(event) {
        const {items} = event.detail;

        NuxeoClient
            .instance()
            .getArchivedSubmissionsAssociatedFiles(items.map(item => item.uid))
            .then((associatedFiles) => {
                EventUtils.raiseEvent('add-to-cart', {
                    items: associatedFiles
                }, this.props);
            })
            .catch(() => {
                window
                    .Materialize
                    .toast('Failed to retrive submissions associated files.', 5000);
                this.loading = false;
            });
    }

    render() {
        const {loading, searchResults, selectedSubmission} = this.state;

        return (
            <div>

                <section className="submissions-view">
                    <Loading hidden={!loading}/>
                    <SearchSidebar
                        reference={element => this._searchSidebar = element}
                        onSearch={event => this.searchTerm = event.detail}
                        jobType={"Legacy Archive"}/>
                    <div className="content">
                        <SubmissionDetails submission={selectedSubmission}/>
                        <Grid
                            entries={searchResults.entries}
                            totalCount={searchResults.totalCount}
                            tableColumns={GRID_COLUMNS}
                            thumbnailMapper={SUBMISSION_THUMBNAIL_MAPPER}
                            displayMode={Grid.DISPLAY_MODE_TABLE}
                            onScrollBottom={this._loadMoreSearchResults}
                            hidden={selectedSubmission}
                            onAddToCart={this._handleAddToCart}/>
                    </div>
                </section>

            </div>
        );
    }
}

export default withRouter(SubmissionsView);